<script setup lang="ts">
const { t: $t } = useI18n()

useHead({
  bodyAttrs: { class: 'dark:bg-neutral-950' }
})

const benefits = [
  { icon: 'i-lucide-rocket', title: 'auth.brand.deployTitle', desc: 'auth.brand.deployDesc' },
  { icon: 'i-lucide-gauge', title: 'auth.brand.paygTitle', desc: 'auth.brand.paygDesc' },
  { icon: 'i-lucide-trending-up', title: 'auth.brand.scaleTitle', desc: 'auth.brand.scaleDesc' },
  { icon: 'i-lucide-headset', title: 'auth.brand.supportTitle', desc: 'auth.brand.supportDesc' },
]

const { privacyUrl, termsUrl, faqUrl } = useLegalLinks()
</script>

<template>
  <div class="lg:h-screen lg:grid lg:grid-cols-2 lg:overflow-hidden bg-white dark:bg-neutral-950">

    <!-- FORM SIDE (left) — primary task lands in the natural reading start -->
    <div class="relative flex flex-col min-h-screen lg:h-screen lg:overflow-y-auto px-4 py-8 sm:px-8 bg-white dark:bg-neutral-950">
      <!-- Logo (mobile only — desktop shows it on the brand panel) -->
      <div class="lg:hidden flex justify-center mb-10">
        <AppLogo size="md" class="transition-transform duration-300 hover:scale-105" />
      </div>

      <div class="flex-1 flex items-center justify-center">
        <div class="w-full max-w-md">
          <slot />
        </div>
      </div>
    </div>

    <!-- BRAND SIDE (right) — desktop only -->
    <aside class="relative hidden lg:flex lg:h-screen flex-col justify-between overflow-hidden text-white p-12 xl:p-16 bg-gradient-to-br from-primary-600 via-primary-800 to-gray-950">
      <!-- soft glowing blobs -->
      <div class="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-primary-400/40 blur-[90px] pointer-events-none" />
      <div class="absolute -bottom-24 -left-12 w-80 h-80 rounded-full bg-primary-500/40 blur-[90px] pointer-events-none" />
      <div class="absolute inset-0 opacity-[0.04] pointer-events-none"
           style="background-image: linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px); background-size: 44px 44px;" />

      <!-- Logo. `surface="dark"` because this panel is dark in both themes —
           and via AppLogo, so a white-label deployment's own mark lands here
           too; it used to import the bundled asset directly. -->
      <AppLogo surface="dark" size="lg" class="relative z-10 w-auto self-start" />

      <!-- Headline + reasons to sign up -->
      <div class="relative z-10 max-w-md">
        <h2 class="text-3xl xl:text-4xl font-bold leading-tight">
          {{ $t('auth.brand.headline') }}
        </h2>
        <p class="mt-4 text-base text-white/70">
          {{ $t('auth.brand.subtitle') }}
        </p>

        <ul class="mt-10 space-y-5">
          <li v-for="b in benefits" :key="b.title" class="flex items-start gap-4">
            <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center ring-1 ring-white/15">
              <UIcon :name="b.icon" class="w-5 h-5 text-white" />
            </div>
            <div>
              <p class="font-semibold leading-snug">{{ $t(b.title) }}</p>
              <p class="text-sm text-white/60 leading-snug">{{ $t(b.desc) }}</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Footer links -->
      <div class="relative z-10 flex items-center gap-6 text-sm text-white/60">
        <a v-if="privacyUrl" :href="privacyUrl" target="_blank" class="hover:text-white transition-colors">{{ $t('auth.brand.privacy') }}</a>
        <a v-if="termsUrl" :href="termsUrl" target="_blank" class="hover:text-white transition-colors">{{ $t('auth.brand.terms') }}</a>
        <a v-if="faqUrl" :href="faqUrl" target="_blank" class="hover:text-white transition-colors">{{ $t('auth.brand.faq') }}</a>
      </div>
    </aside>
  </div>
</template>
